import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { fetchProject, fetchMe, type ProjectDetail } from '../api';
import { RoadmapTab, WorkTab } from './RoadmapTab';
import { ConversationTab } from './ConversationTab';
import { DocsTab } from './DocsTab';
import { RisksPage } from './RisksPage';
import {
  Badge,
  Empty,
  ErrorBox,
  HealthBadge,
  Loading,
  ProgressBar,
  RelativeTime,
  Section,
  Table,
  Value,
} from '../components/ui';

const STATUS_LABEL: Record<string, string> = {
  planning: 'Planificación',
  active: 'Activo',
  blocked: 'Bloqueado',
  paused: 'Pausado',
  maintenance: 'Mantenimiento',
  archived: 'Archivado',
};

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'work', label: 'Trabajo' },
  { id: 'docs', label: 'Documentación' },
  { id: 'conversacion', label: 'Conversación' },
  { id: 'code', label: 'Código' },
  { id: 'delivery', label: 'Entregas' },
  { id: 'risks', label: 'Riesgos' },
] as const;

export function ProjectPage(): JSX.Element {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'resumen';

  const query = useQuery({
    queryKey: ['project', slug],
    queryFn: () => fetchProject(slug),
    refetchInterval: 120_000,
  });
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false, staleTime: 300_000 });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const detail = query.data;
  const m = detail.metrics;

  return (
    <>
      {/* ── Cabecera ─────────────────────────────────────────────────────── */}
      <div className="mb-4">
        <Link to="/projects" className="text-[12px] text-[var(--color-ink-muted)] hover:underline">
          ← Proyectos
        </Link>
        <div className="mt-1 flex flex-wrap items-baseline gap-3">
          <h1 className="text-lg font-semibold">{m.name}</h1>
          <HealthBadge health={m.health} reasons={m.healthReasons} />
          <Badge>{STATUS_LABEL[m.status] ?? m.status}</Badge>
          {/* El estado NO se deriva de nada: lo escribe una persona y luego envejece en
              silencio. `educore-rd` estuvo dos meses marcado «en mantenimiento» mientras
              recibía commits. Decir quién y cuándo es lo que permite dudar de la etiqueta;
              a partir de 60 días se marca, porque es cuando deja de describir algo. */}
          {m.statusSource === 'manual' && (
            <span
              className={
                m.statusAgeDays !== null && m.statusAgeDays > 60
                  ? 'text-[11px] text-amber-700 dark:text-amber-400'
                  : 'text-[11px] text-[var(--color-ink-muted)]'
              }
              title="El estado no se calcula: alguien lo decidió. Una etiqueta vieja deja de describir el proyecto."
            >
              {m.statusAgeDays === null
                ? 'puesto a mano'
                : `puesto a mano por ${m.statusSetBy ?? 'alguien'} ${
                    m.statusAgeDays === 0 ? 'hoy' : `hace ${m.statusAgeDays} día(s)`
                  }`}
              {m.statusAgeDays !== null && m.statusAgeDays > 60 && ' · conviene revisarlo'}
            </span>
          )}
          <Badge tone={m.priority === 'P0' ? 'bad' : m.priority === 'P1' ? 'warn' : 'neutral'}>
            {m.priority}
          </Badge>
        </div>
        {detail.description && (
          <p className="mt-1 max-w-3xl text-[13px] leading-snug text-[var(--color-ink-muted)]">
            {detail.description}
          </p>
        )}

        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[12px]">
          <Field label="Responsable">
            <Value reason="sin responsable asignado">{detail.ownerName}</Value>
          </Field>
          <Field label="Rama principal">
            <code className="font-mono text-[11px]">{m.defaultBranch ?? '—'}</code>
          </Field>
          <Field label="Progreso">
            <ProgressBar pct={m.progressPct} />
          </Field>
          <Field label="Inicio">
            <Value reason="sin fecha de inicio">{detail.startDate}</Value>
          </Field>
          <Field label="Objetivo">
            <Value reason="sin fecha objetivo">{detail.targetDate}</Value>
          </Field>
          <Field label="Actividad">
            <RelativeTime iso={m.lastActivityAt} />
          </Field>
        </dl>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {detail.repos.map((repo) => (
            <a
              key={String(repo.fullName)}
              href={(repo.htmlUrl as string | null) ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="rounded border border-[var(--color-border-subtle)] px-2 py-0.5 text-[11px] hover:border-[var(--color-ink-muted)]"
            >
              {String(repo.fullName)}
              <span className="ml-1 opacity-60">
                {repo.isUnmanaged ? 'no gestionado' : String(repo.visibility)}
              </span>
            </a>
          ))}
          {detail.environments
            .filter((environment) => environment.url)
            .map((environment) => (
              <a
                key={String(environment.name)}
                href={String(environment.url)}
                target="_blank"
                rel="noreferrer"
                className="rounded bg-[var(--color-surface-muted)] px-2 py-0.5 text-[11px] underline decoration-dotted"
              >
                {String(environment.name)}
              </a>
            ))}
        </div>
      </div>

      {/* ── Cinta de resumen ─────────────────────────────────────────────── */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        <Stat
          label="PRs abiertos"
          value={m.prsOpenHuman}
          extra={m.prsOpenBot > 0 ? `+${m.prsOpenBot} bot` : undefined}
        />
        <Stat label="Commits (30 d)" value={m.commits30d} />
        {/* 30 días, no toda la vida del repositorio. El acumulado no baja cuando el CI se
            arregla —Dexter marcaba 51,7 % con 0 fallos en la última semana— y un
            indicador que no se cura se aprende a ignorar. El histórico va debajo. */}
        <Stat
          label="Fallo de CI (30 d)"
          value={m.runFailurePct30d}
          suffix=" %"
          reason={
            m.runFailurePct === null
              ? 'el repositorio no tiene CI configurada'
              : 'sin ejecuciones en los últimos 30 días'
          }
          extra={m.runFailurePct === null ? undefined : `${m.runFailurePct} % histórico`}
          tone={m.runFailurePct30d !== null && m.runFailurePct30d >= 30 ? 'bad' : undefined}
        />
        {/* El rojo lo enciende la rama de producción. El total sigue visible como extra:
            si 17 de 17 fallos están en ramas ya borradas, hay que poder verlo, pero no
            debe pintar de rojo un proyecto donde nadie puede arreglar nada. */}
        <Stat
          label="Fallos 7 d (producción)"
          value={m.runsFailedProd7d}
          tone={m.runsFailedProd7d > 0 ? 'bad' : undefined}
          extra={
            m.runsFailed7d === m.runsFailedProd7d
              ? undefined
              : m.runsFailedLive7d === m.runsFailed7d
                ? `${m.runsFailed7d} en total`
                : `${m.runsFailed7d} en total, ${m.runsFailed7d - m.runsFailedLive7d} en ramas borradas`
          }
        />
        <Stat label="En producción" value={m.tagsProduction} extra="tags" />
        <Stat
          label="Tareas abiertas"
          value={m.tasksOpen}
          reason="este proyecto no tiene tareas registradas todavía"
        />
        <Stat label="Entornos" value={detail.environments.length} />
      </div>

      {/* ── Pestañas ─────────────────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap gap-1 border-b border-[var(--color-border-subtle)]">
        {TABS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setParams(option.id === 'resumen' ? {} : { tab: option.id })}
            className={`-mb-px border-b-2 px-3 py-1.5 text-[13px] ${
              tab === option.id
                ? 'border-[var(--color-ink)] font-medium'
                : 'border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'resumen' && <SummaryTab detail={detail} />}
      {tab === 'roadmap' && me.data && <RoadmapTab slug={slug} me={me.data} />}
      {tab === 'work' && <WorkTab slug={slug} />}
      {tab === 'docs' && <DocsTab slug={slug} me={me.data} />}
      {tab === 'conversacion' && me.data && <ConversationTab projectId={detail.id} me={me.data} />}
      {tab === 'code' && <CodeTab detail={detail} />}
      {tab === 'delivery' && <DeliveryTab detail={detail} />}
      {tab === 'risks' && <RisksPage projectSlug={slug} />}
    </>
  );
}

// ─── Pestañas ───────────────────────────────────────────────────────────────

function SummaryTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  return (
    <>
      {detail.unmanagedCodebases.length > 0 && (
        <Section title="Código sin repositorio remoto">
          <ul className="space-y-1.5">
            {detail.unmanagedCodebases.map((codebase) => (
              <li
                key={String(codebase.slug)}
                className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[12px] leading-snug text-red-900 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200"
              >
                <strong className="font-semibold">■ {String(codebase.vpsPath)}</strong>{' '}
                {codebase.resolvedAt ? 'resuelto.' : 'sigue solo en el disco de esta VPS.'}
                <div className="mt-0.5 opacity-80">
                  {String(codebase.reason ?? '')}
                  {codebase.servesUrl ? ` · sirve ${String(codebase.servesUrl)}` : ''}
                  {codebase.localCommits
                    ? ` · ${String(codebase.localCommits)} commits locales`
                    : ''}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section
        title="Tecnologías detectadas"
        subtitle={
          detail.techDetectedAt
            ? `Leídas de los manifiestos del repositorio · ${new Date(detail.techDetectedAt).toLocaleDateString('es-ES')}`
            : 'Sin detectar todavía'
        }
      >
        {detail.technologies.length === 0 ? (
          <Empty>No se encontró ningún manifiesto reconocible en el repositorio.</Empty>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {detail.technologies.map((tech) => (
              <li key={`${String(tech.technology)}-${String(tech.evidencePath)}`}>
                <span
                  className="inline-block rounded bg-[var(--color-surface-muted)] px-2 py-0.5 text-[11px]"
                  title={`Detectado en ${String(tech.evidencePath)} (${String(tech.evidence)})`}
                >
                  {String(tech.technology)}
                  {tech.version ? (
                    <span className="ml-1 opacity-60">{String(tech.version)}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Entornos" subtitle="Del inventario real de nginx, systemd y bases de datos">
        {detail.environments.length === 0 ? (
          <Empty>Sin entornos registrados.</Empty>
        ) : (
          <Table head={['Entorno', 'URL', 'Ruta en la VPS', 'Servicios', 'Base de datos', 'Rama']}>
            {detail.environments.map((environment) => (
              <tr
                key={String(environment.name)}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2">
                  <span className="font-medium">{String(environment.name)}</span>
                  <div className="text-[11px] text-[var(--color-ink-muted)]">
                    {String(environment.kind)}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {environment.url ? (
                    <a
                      href={String(environment.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] hover:underline"
                    >
                      {String(environment.url).replace('https://', '')}
                    </a>
                  ) : (
                    <Value reason="sin URL registrada">{null}</Value>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  <Value reason="sin ruta registrada">{environment.vpsPath as string | null}</Value>
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  {(environment.systemdUnits as string[]).length > 0
                    ? (environment.systemdUnits as string[]).join(', ').replace(/\.service/g, '')
                    : '—'}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {environment.dbName
                    ? `${String(environment.dbEngine)}/${String(environment.dbName)}`
                    : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  <Value reason="sin rama asociada">
                    {environment.trackedBranch as string | null}
                  </Value>
                </td>
              </tr>
            ))}
          </Table>
        )}
        {detail.environments.some((environment) => environment.notes) && (
          <ul className="mt-2 space-y-0.5 text-[11px] leading-snug text-[var(--color-ink-muted)]">
            {detail.environments
              .filter((environment) => environment.notes)
              .map((environment) => (
                <li key={String(environment.name)}>
                  · <strong>{String(environment.name)}:</strong> {String(environment.notes)}
                </li>
              ))}
          </ul>
        )}
      </Section>

      {detail.docsUrls.length > 0 && (
        <Section title="Documentación disponible" subtitle="Ficheros encontrados en el repositorio">
          <ul className="flex flex-wrap gap-1.5">
            {detail.docsUrls.map((doc) => (
              <li key={doc}>
                <code className="rounded bg-[var(--color-surface-muted)] px-2 py-0.5 text-[11px]">
                  {doc}
                </code>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {detail.notes && (
        <Section title="Notas">
          <p className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-3 py-2 text-[13px] leading-snug">
            {detail.notes}
          </p>
        </Section>
      )}

      <Section title="Actividad reciente">
        {detail.activity.length === 0 ? (
          <Empty>Sin actividad registrada.</Empty>
        ) : (
          <ol className="rounded-lg border border-[var(--color-border-subtle)]">
            {detail.activity.slice(0, 15).map((entry, index) => (
              <li
                key={`${String(entry.kind)}-${index}`}
                className="flex items-baseline gap-3 border-b border-[var(--color-border-subtle)] px-3 py-1.5 text-[12px] last:border-0"
              >
                <span className="w-14 shrink-0 text-[11px] text-[var(--color-ink-muted)]">
                  <RelativeTime iso={String(entry.at)} />
                </span>
                <span className="w-20 shrink-0 text-[11px] text-[var(--color-ink-muted)]">
                  {String(entry.kind)}
                </span>
                <span className="min-w-0 flex-1 truncate">{String(entry.title)}</span>
              </li>
            ))}
          </ol>
        )}
      </Section>
    </>
  );
}

function CodeTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const flagged = detail.branches.filter((branch) => branch.flags.length > 0);
  const open = detail.pulls.filter((pull) => pull.state === 'open');
  const merged = detail.pulls.filter((pull) => pull.merged);

  return (
    <>
      {flagged.length > 0 && (
        <Section
          title="Ramas que piden una decisión"
          subtitle="Divergencia y abandono son problemas distintos: una hay que fusionarla, la otra decidir si se tira"
        >
          <ul className="space-y-1.5">
            {flagged.map((branch) => (
              <li
                key={`${branch.repo}/${branch.name}`}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
              >
                <code className="font-mono font-semibold">{branch.name}</code>
                <span className="ml-2 opacity-70">{branch.repo}</span>
                <ul className="mt-0.5 opacity-90">
                  {branch.flags.map((flag) => (
                    <li key={flag}>▲ {flag}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Ramas" subtitle={`${detail.branches.length} en total`}>
        <Table
          head={['Rama', 'Repositorio', 'Adelante', 'Detrás', 'Último commit', 'PR abierto', '']}
        >
          {detail.branches.map((branch) => (
            <tr
              key={`${branch.repo}/${branch.name}`}
              className="border-b border-[var(--color-border-subtle)] last:border-0"
            >
              <td className="px-3 py-2">
                <code className="font-mono text-[12px]">{branch.name}</code>
                {branch.isDefault && (
                  <span className="ml-1.5">
                    <Badge tone="good">por defecto</Badge>
                  </span>
                )}
                {branch.isProtected && (
                  <span className="ml-1">
                    <Badge>protegida</Badge>
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">{branch.repo}</td>
              <td className="px-3 py-2 tabular-nums">
                {branch.isDefault ? (
                  '—'
                ) : (
                  <Value reason="no se ha comparado con la rama por defecto">
                    {branch.aheadBy}
                  </Value>
                )}
              </td>
              <td className="px-3 py-2 tabular-nums">
                {branch.isDefault ? (
                  '—'
                ) : (
                  <Value reason="no se ha comparado con la rama por defecto">
                    {branch.behindBy}
                  </Value>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <RelativeTime iso={branch.headCommitAt} />
              </td>
              <td className="px-3 py-2 text-[12px]">
                {branch.isDefault ? '—' : branch.hasOpenPr ? 'sí' : 'no'}
              </td>
              <td className="px-3 py-2">
                {branch.flags.length > 0 && (
                  <span
                    className="text-[11px] text-amber-700 dark:text-amber-400"
                    title={branch.flags.join(' · ')}
                  >
                    ▲
                  </span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section
        title="Pull requests"
        subtitle={`${open.length} abiertos · ${merged.length} fusionados en 30 días`}
      >
        {detail.pulls.length === 0 ? (
          <Empty>Este proyecto no usa pull requests.</Empty>
        ) : (
          <Table head={['#', 'Título', 'Autor', 'Rama', 'Diff', 'Revisión', 'Estado']}>
            {detail.pulls.map((pull) => (
              <tr
                key={`${String(pull.repo)}#${String(pull.number)}`}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2 tabular-nums text-[var(--color-ink-muted)]">
                  {String(pull.number)}
                </td>
                <td className="px-3 py-2">
                  <a
                    href={(pull.htmlUrl as string | null) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {String(pull.title)}
                  </a>
                  {pull.isBotAuthor ? (
                    <span className="ml-1.5">
                      <Badge>bot</Badge>
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-[12px]">{String(pull.author ?? '—')}</td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  {String(pull.headRef)} → {String(pull.baseRef)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-[11px] tabular-nums">
                  {pull.additions === null ? (
                    '—'
                  ) : (
                    <>
                      <span className="text-emerald-700 dark:text-emerald-400">
                        +{String(pull.additions)}
                      </span>{' '}
                      <span className="text-red-700 dark:text-red-400">
                        −{String(pull.deletions ?? 0)}
                      </span>
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {Number(pull.reviewCount) > 0 ? (
                    `${String(pull.reviewCount)} reseña(s)`
                  ) : (
                    <span
                      className="text-[var(--color-ink-muted)]"
                      title="Nadie ha revisado este pull request"
                    >
                      sin revisar
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-[12px]">
                  {pull.merged ? (
                    <Badge tone="good">fusionado</Badge>
                  ) : (
                    <span
                      className={
                        Number(pull.ageDays) > 7 ? 'text-amber-700 dark:text-amber-400' : undefined
                      }
                    >
                      abierto {String(pull.ageDays)} d
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Commits recientes"
        subtitle={`${detail.commits.length} más recientes de la rama por defecto`}
      >
        {detail.commits.length === 0 ? (
          <Empty>Sin commits espejados.</Empty>
        ) : (
          <Table head={['Cuándo', 'SHA', 'Tipo', 'Asunto', 'Autor', '']}>
            {detail.commits.map((commit) => (
              <tr
                key={String(commit.sha)}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={commit.committedAt as string | null} />
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-[var(--color-ink-muted)]">
                  {String(commit.shortSha)}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {commit.type ? (
                    <Badge
                      tone={
                        commit.type === 'feat' ? 'good' : commit.type === 'fix' ? 'warn' : 'neutral'
                      }
                    >
                      {String(commit.type)}
                      {commit.scope ? `(${String(commit.scope)})` : ''}
                    </Badge>
                  ) : (
                    <span
                      className="text-[var(--color-ink-muted)]"
                      title="El mensaje no sigue Conventional Commits: no se adivina el tipo"
                    >
                      —
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {commit.htmlUrl ? (
                    <a
                      href={String(commit.htmlUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline"
                    >
                      {String(commit.subject)}
                    </a>
                  ) : (
                    String(commit.subject)
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
                  {String(commit.authorLogin ?? commit.authorName ?? '—')}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {commit.isBreaking ? <Badge tone="bad">rompe</Badge> : null}
                  {commit.source === 'git' ? (
                    <span title="Leído del clon local, no de la API de GitHub">
                      <Badge>git</Badge>
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

function DeliveryTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const lifecycle = detail.tagStats.byLifecycle;

  return (
    <>
      <Section title="Ejecuciones de CI" subtitle={`${detail.runs.length} más recientes`}>
        {detail.runs.length === 0 ? (
          <Empty>Este repositorio no tiene integración continua configurada.</Empty>
        ) : (
          <Table head={['Cuándo', 'Workflow', 'Rama', 'Evento', 'Resultado', 'Duración']}>
            {detail.runs.map((run) => (
              <tr
                key={String(run.id)}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={run.startedAt as string | null} />
                </td>
                <td className="px-3 py-2">
                  <a
                    href={(run.htmlUrl as string | null) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {String(run.name ?? 'workflow')}
                  </a>
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  {String(run.branch ?? '—')}
                  {run.isDefaultBranch ? (
                    <span className="ml-1 opacity-60">(por defecto)</span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
                  {String(run.event ?? '—')}
                </td>
                <td className="px-3 py-2">
                  {run.conclusion === 'success' ? (
                    <Badge tone="good">correcto</Badge>
                  ) : run.conclusion === 'failure' ? (
                    <Badge tone="bad">fallo</Badge>
                  ) : (
                    <Badge>{String(run.conclusion ?? run.status ?? '—')}</Badge>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums text-[11px]">
                  {run.durationMs === null ? '—' : `${Math.round(Number(run.durationMs) / 1000)} s`}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Tags de despliegue"
        subtitle="En estos proyectos los tags marcan despliegues, no versiones: es la única señal de qué está en producción que existe en GitHub"
      >
        {detail.tags.length === 0 ? (
          <Empty>Este proyecto no marca despliegues con tags.</Empty>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap gap-1.5 text-[11px]">
              {Object.entries(lifecycle).map(([key, count]) => (
                <Badge
                  key={key}
                  tone={
                    key === 'deployed' || key === 'documented'
                      ? 'good'
                      : key === 'applied'
                        ? 'warn'
                        : 'neutral'
                  }
                >
                  {key}: {count}
                </Badge>
              ))}
              <span className="text-[var(--color-ink-muted)]">
                (de los {detail.tagStats.shown} más recientes)
              </span>
            </div>
            <Table head={['Tag', 'Tipo', 'Ciclo de vida', 'En producción', 'Commit', 'Cuándo']}>
              {detail.tags.map((tag) => (
                <tr
                  key={`${String(tag.repo)}/${String(tag.name)}`}
                  className="border-b border-[var(--color-border-subtle)] last:border-0"
                >
                  <td className="px-3 py-2 font-mono text-[11px]">{String(tag.name)}</td>
                  <td className="px-3 py-2 text-[11px]">{String(tag.kind ?? '—')}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {tag.lifecycle ? (
                      String(tag.lifecycle)
                    ) : (
                      <span
                        className="text-[var(--color-ink-muted)]"
                        title="El nombre no sigue la convención -applied/-deployed/-documented"
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {tag.impliesProduction ? (
                      <Badge tone="good">sí</Badge>
                    ) : (
                      <span
                        className="text-[11px] text-[var(--color-ink-muted)]"
                        title="Solo -deployed y -documented garantizan producción; -applied significa que el cambio está en la rama"
                      >
                        no
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[var(--color-ink-muted)]">
                    {String(tag.shortSha)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                    <RelativeTime iso={tag.taggedAt as string | null} />
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Section>

      <Section title="Releases de GitHub">
        {detail.releases.length === 0 ? (
          <Empty>
            Este proyecto no publica releases en GitHub. El despliegue se marca con tags.
          </Empty>
        ) : (
          <Table head={['Tag', 'Nombre', 'Publicado']}>
            {detail.releases.map((release) => (
              <tr
                key={String(release.tagName)}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2 font-mono text-[11px]">{String(release.tagName)}</td>
                <td className="px-3 py-2">{String(release.name ?? '—')}</td>
                <td className="px-3 py-2">
                  <RelativeTime iso={release.publishedAt as string | null} />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Deployments de GitHub">
        {detail.deployments.length === 0 ? (
          <Empty>
            Este proyecto no usa GitHub Deployments: el despliegue es manual o por script en la VPS.
            La fuente de verdad de «último despliegue» es la tabla de entornos.
          </Empty>
        ) : (
          <Table head={['Entorno', 'Ref', 'Estado', 'Cuándo']}>
            {detail.deployments.map((deployment) => (
              <tr
                key={String(deployment.id)}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2">{String(deployment.environment ?? '—')}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{String(deployment.ref ?? '—')}</td>
                <td className="px-3 py-2 text-[12px]">{String(deployment.state ?? '—')}</td>
                <td className="px-3 py-2">
                  <RelativeTime iso={deployment.createdAt as string | null} />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Estado de sincronización"
        subtitle="Qué datos hay, cuándo se leyeron y qué no está disponible"
      >
        <Table head={['Recurso', 'Elementos', 'Última vez', 'Nota']}>
          {detail.syncState.map((state) => (
            <tr
              key={`${String(state.repo)}-${String(state.resource)}`}
              className="border-b border-[var(--color-border-subtle)] last:border-0"
            >
              <td className="px-3 py-2 font-mono text-[11px]">{String(state.resource)}</td>
              <td className="px-3 py-2 tabular-nums">{String(state.items ?? 0)}</td>
              <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                <RelativeTime iso={state.lastSuccessAt as string | null} />
              </td>
              <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
                {Number(state.failures) > 0 ? (
                  <span className="text-red-700 dark:text-red-400">
                    {String(state.failures)} fallo(s): {String(state.lastError ?? '')}
                  </span>
                ) : (
                  String(state.skippedReason ?? '')
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Section>
    </>
  );
}

// ─── Piezas ─────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-[var(--color-ink-muted)]">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Stat({
  label,
  value,
  extra,
  suffix = '',
  reason,
  tone,
}: {
  label: string;
  value: number | null;
  extra?: string;
  suffix?: string;
  reason?: string;
  tone?: 'bad';
}): JSX.Element {
  return (
    <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-2.5 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
        {label}
      </div>
      {value === null ? (
        <div className="text-[13px] text-[var(--color-ink-muted)]" title={reason}>
          no disponible
        </div>
      ) : (
        <div
          className={`text-[17px] font-semibold tabular-nums ${
            tone === 'bad' ? 'text-red-700 dark:text-red-400' : ''
          }`}
        >
          {value.toLocaleString('es-ES')}
          {suffix}
          {extra && <span className="ml-1 text-[11px] font-normal opacity-60">{extra}</span>}
        </div>
      )}
    </div>
  );
}
